<template>
    <v-dialog v-model="dialog" max-width="700px" persistent scrollable>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div class="booking-dialog-title">
                        <v-icon class="mr-2" color="white" size="20">mdi-email-send-outline</v-icon>
                        Send Per Person Deposits Reminder
                    </div>
                    <v-btn icon dark @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <v-card-text class="pa-6">
                <div class="booking-section mb-4">
                    <div class="booking-section-header mb-1">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-format-title</v-icon>
                        SUBJECT
                    </div>
                    <v-text-field v-model="subject" outlined dense placeholder="Enter email subject"
                        hide-details></v-text-field>
                </div>
                <div class="booking-section">
                    <div class="booking-section-header mb-2">
                        <v-icon size="14" color="#3988AA" class="mr-1">mdi-text</v-icon>
                        EMAIL CONTENT
                    </div>
                    <wysiwyg v-model="body" placeholder="Compose the deposits reminder email..." />
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">Cancel</v-btn>
                <v-btn dark color="#3988AA" :loading="sending" :disabled="!subject || !body" @click="sendEmail()">
                    <v-icon small left>mdi-send</v-icon>
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex"
import { personPaymentStatus } from "@/utils/eventPayments"

export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        lead: {
            type: Object,
            default: () => ({})
        },
        event: {
            type: Object,
            default: () => ({ invitees: [], quotes: [] })
        },
        company_id: {
            type: Number,
            default: null
        }
    },
    data: () => ({
        subject: '',
        body: '',
        sending: false,
    }),
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        },
        unpaidInvitees() {
            return (this.event.invitees || []).filter(item => this.getInviteeStatus(item) === 'Unpaid')
        }
    },
    mounted() {
        if (this.show) {
            this.subject = this.buildDefaultSubject()
            this.body = this.buildDefaultBody()
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.sending = false
                this.subject = this.buildDefaultSubject()
                this.body = this.buildDefaultBody()
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close')
        },
        // Same rule as ManageInvitees.vue's getInviteeStatus() (utils/eventPayments.js) so the
        // "Unpaid" list here stays consistent with what the organiser sees on Manage Invitees.
        getInviteeStatus(item) {
            return personPaymentStatus(this.event, item.id, this.lead)
        },
        buildDefaultSubject() {
            return `Deposit reminder for ${this.event?.name || ''}`
        },
        buildUnpaidInviteesTable() {
            if (!this.unpaidInvitees.length) {
                return `<p style="text-align:left;color:black;font-size:16px;">Great news — everyone in your group has paid their deposit!</p>`
            }
            const rows = this.unpaidInvitees.map(inv => `
                <tr>
                    <td style="padding:10px 12px;border:1px solid #DDDDDD;font-size:14px;color:#000000;word-break:break-word;">${inv.name || ''}</td>
                    <td style="padding:10px 12px;border:1px solid #DDDDDD;font-size:14px;color:#000000;word-break:break-word;">${inv.email || ''}</td>
                </tr>
            `).join('')
            return `
                <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:100%;border-collapse:collapse;table-layout:fixed;">
                    <tr>
                        <th style="width:50%;padding:10px 12px;border:1px solid #DDDDDD;background-color:#3988AA;color:#FFFFFF;font-size:14px;text-align:left;">Invitee Name</th>
                        <th style="width:50%;padding:10px 12px;border:1px solid #DDDDDD;background-color:#3988AA;color:#FFFFFF;font-size:14px;text-align:left;">Email</th>
                    </tr>
                    ${rows}
                </table>
            `
        },
        buildDefaultBody() {
            const lead = this.lead || {}
            const event = this.event || {}
            const advisorName = this.get_user?.name || ''
            const companyNumber = event.company?.number || ''

            return `
                <div style="text-align:center;font-size:28px;color:black;"><b>Hello ${lead.first_name || ''},</b></div>
                <br>
                <p style="text-align:left;color:black;font-size:16px;">
                    ${advisorName} here.
                    <br><br>
                    This is a friendly reminder that per-person deposits are due to secure your group's booking, and some of your invitees have not yet paid theirs.
                    <br><br>
                    Please encourage your group to pay their deposit as soon as possible, as spaces and pricing can't be guaranteed for anyone who hasn't paid.
                    <br><br>
                    If you have any questions, or would like to pay on behalf of an invitee, call us on ${companyNumber} (option 2) during office hours (Mon - Fri 9am - 5:30pm).
                </p>
                <br>
                ${this.buildUnpaidInviteesTable()}
            `
        },
        async sendEmail() {
            this.sending = true
            try {
                const payload = {
                    company_id: this.company_id,
                    lead: this.lead,
                    event: this.event,
                    subject: this.subject,
                    body: this.body,
                    email_type: 'organiser_deposits_reminder',
                }
                const result = await (await this.$axios.post('admin/events/send_organiser_email', payload)).data
                if (result.response == true) {
                    this.$toast.success(result.message)
                    this.closeDialog()
                } else {
                    this.$toast.error('Failed to send deposits reminder email')
                }
            } catch (error) {
                this.$toast.error('Failed to send deposits reminder email')
            } finally {
                this.sending = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-section-header {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    color: #3D3D3D;
    display: flex;
    align-items: center;
}
</style>
